import React from 'react';

import { ArrayToDD, dropdownMapper } from '@pharmaplan/common/helpers/utils';
import {
  bankAccountMaxLengths,
  HelpTypes,
  IAdminSoftware,
  IAllLanguages,
  IContactPreferences,
  IPharmacistCapacity,
  IProfessionalCapacity,
  IProvinces,
  PermissionsOfAdmin,
  ServiceTypes,
} from '@pharmaplan/common';
import ServiceHandler, {
  idStore,
} from '@pharmaplan/common/helpers/ServiceHandler';
import { FormikProps } from 'formik';
import {
  Constants,
  InputTypes,
  SoftwareExperience,
  daysDropdown,
  telephoneInputMask,
} from '../../../../../helpers/Constants';
import strings from '../../../../../localization';
import { LanguageLevel, years } from '../../../../Profile/ProfileForm/Forms';

import { professionalSelfServiceKeys } from '../PharmacistViewProfile/helper';
import genericClasses from '../../../../../theme/GenericClasses';
import CustomDatePicker from '../../../../common/CustomDatePicker';

import classes from './style';

const { billing, incorporated: incorporatedKey } = professionalSelfServiceKeys;
const campaignKey = 'campaignKey';

interface IPharmacyEditConfig {
  languages: IAllLanguages[];
  can: (permission: PermissionsOfAdmin) => boolean;
  pharmacistCapacities: IPharmacistCapacity[] | IProfessionalCapacity[];
  prefferedReplacementDurations: string | string[];
  selectedSoftwares: Array<IAdminSoftware>;
  provinces: Array<IProvinces>;
  preventChangeEmail: boolean;
  contactPreferences: Array<IContactPreferences>;
  handleEdit: () => void;
  isFromPendingUsers: boolean;
  isIncorporated?: boolean;
  userType?: HelpTypes;
  noEntitlement?: boolean;
  formik: FormikProps<any>;
}

export const softwareDropdown = SoftwareExperience().map(
  (experience, index) =>
    ({
      key: index + 1,
      label: experience,
    }),
);

export const handleSoftware = (data: Array<IAdminSoftware>) =>
  data.map((item) => {
    const { software, softwareId } = item ?? {};
    const { name } = software ?? {};

    return {
      label: name,
      type: Constants.formInputType.dropdown,
      name: softwareId,
      id: softwareId,
      menuItems: softwareDropdown,
    };
  });

export const pharmacistEditConfig = ({
  languages,
  can,
  pharmacistCapacities,
  prefferedReplacementDurations,
  selectedSoftwares,
  provinces,
  preventChangeEmail,
  contactPreferences,
  handleEdit,
  isIncorporated,
  isFromPendingUsers,
  noEntitlement,
  userType,
  formik,
}: IPharmacyEditConfig) => {
  const canAllocateCredits = can(PermissionsOfAdmin.AdditionalCreditAllocation);
  const isSelfService = ServiceHandler.getService() === ServiceTypes.self;
  const isTechnician = `${userType}` === HelpTypes.technicians;
  const isPharmacist = `${userType}` === HelpTypes.pharmacist;

  const incorporatedInfo = [
    {
      label: strings.companyName,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateCompanyName',
      id: 'corporateCompanyName',
      requireLabel: true,
    },
    {
      label: strings.streetAddress,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateStreetAddress',
      id: 'corporateStreetAddress',
    },
    {
      label: strings.city,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateCity',
      id: 'corporateCity',
    },
    {
      label: strings.province,
      xs: 6,
      name: 'corporateProvince',
      id: 'corporateProvince',
      type: Constants.formInputType.dropdown,
      menuItems: dropdownMapper(provinces ?? [], 'key', 'name'),
    },
    {
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporatePostalCode',
      id: 'corporatePostalCode',
      capitalize: true,
      customType: InputTypes.postalCode,
      label: `${strings.postalCode} (${strings.postalCodeHint})`,
      maxLength: 10,
      placeholder: ' ',
    },
    {
      label: strings.telephoneNumber,
      xs: 6,
      name: 'corporatePhone',
      id: 'corporatePhone',
      type: Constants.formInputType.masked,
      mask: telephoneInputMask,
    },
    {
      label: strings.tpsNumber,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tps',
      id: 'tps',
      requireLabel: true,
    },
    {
      label: strings.tvqNumber,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tvq',
      id: 'tvq',
      requireLabel: true,
    },
  ];

  const nonIncorporatedInfo = [
    {
      label: strings.formatString(
        strings.ifApplicableFormat,
        strings.tpsNumber,
      ) as string,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tps',
      id: 'tps',
    },
    {
      label: strings.formatString(
        strings.ifApplicableFormat,
        strings.tvqNumber,
      ) as string,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tvq',
      id: 'tvq',
    },
  ];

  let infoForm: unknown[] = [];

  if (isIncorporated) {
    infoForm = incorporatedInfo;
  } else {
    infoForm = nonIncorporatedInfo;
  }

  let arr = [
    {
      title: strings.general,
      key: 1,
      form: () =>
        [
          {
            label: strings.email,
            type: Constants.formInputType.changeEmail,
            requireLabel: true,
            disabled: preventChangeEmail,
            name: 'email',
          },
          {
            label: strings.firstName,
            type: Constants.formInputType.text,
            requireLabel: true,
            name: 'firstName',
          },
          {
            label: strings.lastName,
            type: Constants.formInputType.text,
            requireLabel: true,
            name: 'lastName',
          },
          {
            label: strings.preferredLanguage,
            type: Constants.formInputType.dropdown,
            name: 'preferdLanguage',
            requireLabel: true,
            id: 'preferdLanguage',
            menuItems: dropdownMapper(languages, 'languageId', 'name'),
          },
          {
            label: strings.contactPreferences,
            type: Constants.formInputType.dropdown,
            multiple: true,
            name: 'selectedContactPreferences',
            id: 'selectedContactPreferences',
            menuItems: dropdownMapper(
              contactPreferences,
              'contactPreferenceId',
              'name',
            ),
          },
        ],
    },
    {
      title: strings.professionalInfo,
      key: 2,
      form: () =>
        [
          isPharmacist && {
            label: strings.licenseNumber,
            type: Constants.formInputType.text,
            requireLabel: true,
            name: 'licenseNumber',
            maxLength: 8,
          },
          {
            label: strings.professionalCapacity,
            type: Constants.formInputType.dropdown,
            name: idStore.capacityId,
            requireLabel: true,
            id: idStore.capacityId,
            menuItems: dropdownMapper(
            pharmacistCapacities as IProfessionalCapacity[],
            idStore.capacityId as keyof IProfessionalCapacity,
            'description',
            ),
          },
          isTechnician && {
            label: strings.diploma,
            infoText: strings.diplomaInfo,
            type: Constants.formInputType.text,
            xs: 6,
            maxLength: 300,
            name: 'diploma',
            id: 'diploma',
            requireLabel: true,
          },
          isTechnician && {
            label: strings.educationalInstitute,
            infoText: strings.educationalInstituteInfo,
            type: Constants.formInputType.text,
            xs: 6,
            maxLength: 300,
            name: 'instituteName',
            id: 'instituteName',
            requireLabel: true,
          },
          isTechnician && {
            label: strings.graduationYear,
            type: Constants.formInputType.dropdown,
            xs: 6,
            name: 'techGraduationYear',
            id: 'techGraduationYear',
            menuItems: years,
            requireLabel: true,
          },
          (isPharmacist || isTechnician) && {
            label: isTechnician
              ? strings.techExperienceSince
              : strings.yearLicensed,
            type: Constants.formInputType.dropdown,
            name: 'graduationYear',
            id: 'graduationYear',
            menuItems: years,
            requireLabel: true,
          },
          {
            label: strings.yearsOfExperience,
            type: Constants.formInputType.text,
            requireLabel: true,
            disabled: true,
            name: 'yearsOfExperience',
          },
          {
            label: strings.englishLevel,
            type: Constants.formInputType.dropdown,
            name: 'englishLevel',
            id: 'englishLevel',
            menuItems: LanguageLevel(),
          },
          {
            label: strings.frenchLevel,
            type: Constants.formInputType.dropdown,
            name: 'frenchLevel',
            id: 'frenchLevel',
            menuItems: LanguageLevel(),
          },
          {
            label: strings.otherLanguage,
            type: Constants.formInputType.text,
            name: 'otherLanguages',
            id: 'otherLanguages',
          },
          {
            label: strings.areYouIncorporated,
            type: Constants.formInputType.checkbox,
            name: 'incorporated',
            id: 'incorporated',
            customClass: classes.checkPosition,
            labelClass: classes.checkboxText,
          },
          isPharmacist && {
            label: strings.familiarWithMethadonePreparation,
            type: Constants.formInputType.checkbox,
            name: 'isMethadonePrepKnowledge',
            id: 'isMethadonePrepKnowledge',
            customClass: classes.checkPosition,
            labelClass: classes.checkboxText,
          },
          isPharmacist && {
            label: strings.comfortableWithDispillVerification,
            type: Constants.formInputType.checkbox,
            name: 'isComfortDispillVerification',
            id: 'isComfortDispillVerification',
            customClass: classes.checkPosition,
            labelClass: classes.checkboxText,
          },
          isPharmacist && {
            label: strings.doYouHaveOwnDsqKey,
            type: Constants.formInputType.checkbox,
            name: 'isDSQKeyAvailable',
            id: 'isDSQKeyAvailable',
            customClass: classes.checkPosition,
            labelClass: classes.checkboxText,
          },
        ],
    },
    {
      key: incorporatedKey,
      title: strings.incorporated,
      form: () =>
        infoForm,
    },
    {
      key: billing,
      title: strings.billing,
      form: () =>
        [
          {
            id: 'institutionNumber',
            name: 'institutionNumber',
            type: Constants.formInputType.text,
            label: strings.institutionNo,
            maxLength: bankAccountMaxLengths.institutionNumber,
          },
          {
            id: 'branchNumber',
            name: 'branchNumber',
            type: Constants.formInputType.text,
            label: strings.branchNo,
            maxLength: bankAccountMaxLengths.branchNumber,
          },
          {
            id: 'accountNumber',
            name: 'accountNumber',
            type: Constants.formInputType.confirmTextField,
            label: strings.accountNo,
            onClick: handleEdit,
            maxLength: bankAccountMaxLengths.accountNumber,
          },
        ],
    },
    {
      title: strings.softwareFamiliarity,
      key: 3,
      form: () =>
        handleSoftware(selectedSoftwares),
    },
    {
      title: strings.contactDetails,
      key: 4,
      form: () =>
        [
          {
            label: strings.address,
            type: Constants.formInputType.text,
            name: 'address',
            requireLabel: true,
          },
          {
            label: strings.city,
            type: Constants.formInputType.text,
            name: 'city',
            requireLabel: true,
          },
          {
            type: Constants.formInputType.dropdown,
            name: 'province',
            menuItems: dropdownMapper(provinces, 'key', 'name'),
            label: strings.province,
          },
          {
            label: strings.postalCode,
            type: Constants.formInputType.text,
            name: 'postalCode',
            requireLabel: true,
          },
          {
            label: strings.homePhone,
            type: Constants.formInputType.masked,
            mask: telephoneInputMask,
            name: 'phone',
            requireLabel: true,
          },
          {
            label: strings.mobile,
            type: Constants.formInputType.masked,
            mask: telephoneInputMask,
            name: 'mobile',
            requireLabel: true,
          },
        ],
    },
    {
      title: strings.preferences,
      key: 5,
      form: () =>
        [
          {
            label: strings.preferredWorkingDays,
            type: Constants.formInputType.dropdown,
            name: 'preferredWorkingDays',
            id: 'preferredWorkingDays',
            multiple: true,
            menuItems: daysDropdown(),
          },
          {
            label: strings.preferredReplacementDuration,
            type: Constants.formInputType.dropdown,
            name: 'preferredReplacementDuration',
            id: 'preferredReplacementDuration',
            menuItems: ArrayToDD(
              (prefferedReplacementDurations as Array<string>) || [],
            ),
          },
          {
            name: 'prefferedDistanceRange',
            label: strings.maxTravelDistance,
            suffix: 'Km',
            type: Constants.formInputType.text,
          },
          {
            label: strings.remoteRegions,
            type: Constants.formInputType.switch,
            name: 'remoteRegion',
            id: 'remoteRegion',
            xs: 4,
            labelClass: genericClasses.formToggle,
          },
          {
            label: strings.iAlsoWantToRequestMulti,
            type: Constants.formInputType.switch,
            name: 'includeShiftContract',
            id: 'includeShiftContract',
            xs: 4,
            labelClass: genericClasses.formToggle,
          },
          {
            label: strings.bookingInvitationEmails,
            type: Constants.formInputType.switch,
            name: 'sendBookingInvitationReminder',
            id: 'sendBookingInvitationReminder',
            xs: 4,
            labelClass: genericClasses.formToggle,
          },
          !isSelfService && {
            label: strings.availabilityReminder,
            type: Constants.formInputType.checkbox,
            name: 'availabilityReminder',
            id: 'availabilityReminder',
            customClass: classes.checkPosition,
            labelClass: classes.checkboxText,
          },
        ],
    },
    {
      title: strings.campaignDetails,
      key: campaignKey,
      form: () =>
        [
          {
            key: 'promoHourlyRate',
            label: strings.promoHourlyBonus,
            type: Constants.formInputType.text,
            requireLabel: true,
            suffix: '$',
            name: 'promoHourlyRate',
            id: 'promoHourlyRate',
          },
          {
            key: 'totalCredits',
            label: strings.totalCredits,
            type: Constants.formInputType.text,
            requireLabel: true,
            suffix: '$',
            name: 'totalCredits',
            id: 'totalCredits',
          },
          {
            xs: 3,
            key: '4',
            name: 'validUntil',
            label: strings.creditsValidUntil,
            type: Constants.formInputType.component,
            renderComponent: () =>
              (
                <CustomDatePicker
                  label={strings.creditsValidUntil}
                  name="validUntil"
                  readOnly
                  error={
                (formik.touched.validUntil
                  && formik.errors.validUntil) as string
              }
                  formik={formik}
                />
              ),
          },
        ],
    },
  ];

  if (isSelfService || noEntitlement || !canAllocateCredits) {
    arr = arr.filter((item) =>
      item.key !== campaignKey);
  }

  if (!isSelfService) {
    return arr.filter(
      (item) =>
        !Object.values(professionalSelfServiceKeys).includes(
          item.key as string,
        ),
    );
  }

  if (isFromPendingUsers) {
    return arr.filter(
      (item) =>
        item.key !== professionalSelfServiceKeys.billing,
    );
  }
  return arr;
};
